import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/router'
import { RecaptchaHandle } from 'shared/components/recaptcha'
import { BadRequest } from 'shared/errors/bad-request'
import { useToggle } from 'shared/hooks/use-toggle'
import { registerConfirm } from 'modules/authentication/api/register-confirm-api'
import { useCheckPasswordMatch } from 'modules/authentication/hooks/use-check-password-match'
import { RegisterConfirmFormInterface } from 'modules/authentication/types/post-register-interface'

export const useRegisterConfirmForm = () => {
  const router = useRouter()
  const { registerHashPath } = router.query
  const [isLoading, setIsLoading] = useState(false)
  const checkPasswordMatch = useCheckPasswordMatch()
  const [isRecaptchaError, toggleRecaptchaError] = useToggle(false)
  const recaptchaRef = useRef<RecaptchaHandle>(null)

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    getValues,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<RegisterConfirmFormInterface>()

  useEffect(() => {
    clearErrors()
  }, [router.locale])

  // Need for autocomplete in firefox
  useEffect(() => {
    document.addEventListener('animationstart', event => {
      if (event.animationName === 'onAutoFill') {
        const input = event.target as HTMLInputElement
        setValue(input.name as keyof RegisterConfirmFormInterface, input.value)
      }
    })
  }, [])

  const onSubmit = async (values: RegisterConfirmFormInterface) => {
    try {
      setIsLoading(true)
      const { confirmPlainPassword, ...submitValues } = values
      const response = await registerConfirm({
        ...submitValues,
        token: registerHashPath as string,
      })
      const redirect = response.data?.redirect
      if (redirect) {
        router.push(redirect, redirect, { locale: false })
      }
    } catch (e: unknown) {
      if (e instanceof BadRequest) {
        const { fields, common } = e.errors || {}
        if (fields) {
          Object.entries(fields).forEach(([key, messages]) => {
            setError(key as keyof RegisterConfirmFormInterface, {
              message: messages?.join('\n'),
            })
          })
        }
        if (common) {
          setError('root', { message: common?.join('\n') })
        }
        if (fields?.captcha) {
          recaptchaRef.current?.reset()
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    handleSubmit,
    onSubmit,
    control,
    setValue,
    getValues,
    errors,
    isLoading: isLoading || isSubmitting,
    checkPasswordMatch,
    recaptchaRef,
    isRecaptchaError,
    toggleRecaptchaError,
  }
}
